// Run Time Layer - Actual lived experiences delivering value

export interface RunTimeInstance {
  id: string;
  name: string;
  description: string;
  basedOnTemplate: string; // id from designTimeTemplates
  templateName: string; // for display
  instanceType: string;
  client?: string;
  date: string;
  participants: number;
  outcomes: string[];
  feedback?: string;
  evidenceMetrics?: Record<string, string | number>;
  status: 'completed' | 'in-progress' | 'scheduled';
}

export const runTimeInstances: RunTimeInstance[] = [
  // From DEGAP Workshop Design
  {
    id: 'rt-001',
    name: 'Q4 2024 Product Prioritization Workshop - TechCorp',
    description: 'Two-day workshop with TechCorp product team to prioritize Q1 2025 roadmap',
    basedOnTemplate: 'dt-001',
    templateName: 'DEGAP Workshop Design (2-day)',
    instanceType: 'Live Workshop',
    client: 'TechCorp Inc.',
    date: '2024-10-15',
    participants: 12,
    outcomes: [
      'Aligned on top 5 product initiatives for Q1',
      'Identified and resolved 3 major stakeholder conflicts',
      'Created shared mental model of prioritization criteria',
      'Committed to monthly DEGAP check-ins'
    ],
    feedback: 'Team reported 95% satisfaction. CEO noted this was the clearest roadmap prioritization session in company history.',
    evidenceMetrics: {
      'Pre-workshop alignment score': 4.2,
      'Post-workshop alignment score': 8.7,
      'Number of initiatives deprioritized': 23,
      'Time to decision': '2 days (vs. 3 weeks typical)',
      'Follow-up execution rate': '92%'
    },
    status: 'completed'
  },
  {
    id: 'rt-002',
    name: 'Leadership Retreat - GrowthCo',
    description: 'Executive team workshop applying DEGAP to strategic initiatives',
    basedOnTemplate: 'dt-001',
    templateName: 'DEGAP Workshop Design (2-day)',
    instanceType: 'Executive Retreat',
    client: 'GrowthCo',
    date: '2024-11-20',
    participants: 8,
    status: 'scheduled',
    outcomes: [],
    evidenceMetrics: {}
  },

  // From Individual Prioritization Assessment
  {
    id: 'rt-003',
    name: 'Sarah Martinez - Personal Assessment',
    description: 'Individual contributor at FinTech startup completing capability assessment',
    basedOnTemplate: 'dt-005',
    templateName: 'Individual Prioritization Assessment',
    instanceType: 'Online Assessment',
    date: '2024-09-28',
    participants: 1,
    outcomes: [
      'Identified Situational Awareness as top strength (9.2/10)',
      'Revealed opportunity in Tradeoff Intelligence (5.1/10)',
      'Received 12-week development plan',
      'Enrolled in Tradeoff Intelligence micro-course'
    ],
    feedback: 'The assessment helped me understand why I struggle with saying no. The radar chart made it crystal clear.',
    evidenceMetrics: {
      'Overall capability score': 7.3,
      'Top capability': 'Situational Awareness (9.2)',
      'Development area': 'Tradeoff Intelligence (5.1)',
      'Time to complete': '18 minutes',
      'Development plan acceptance': 'yes'
    },
    status: 'completed'
  },
  {
    id: 'rt-004',
    name: 'Peak Priorities Public Assessment - October 2024',
    description: 'Public launch of Individual Prioritization Assessment',
    basedOnTemplate: 'dt-005',
    templateName: 'Individual Prioritization Assessment',
    instanceType: 'Public Web Application',
    date: '2024-10-01',
    participants: 247,
    outcomes: [
      '247 assessments completed in first month',
      'Average completion time: 16.5 minutes',
      'Top capability across all users: Rhythm & Ritual (7.8 avg)',
      'Lowest capability across all users: Cost of Delay Thinking (5.9 avg)',
      '42% of users requested coaching follow-up'
    ],
    evidenceMetrics: {
      'Total completions': 247,
      'Completion rate': '78%',
      'Average overall score': 6.8,
      'NPS score': 67,
      'Coaching conversion': '18%'
    },
    status: 'in-progress'
  },

  // From Six Kinds Assessment
  {
    id: 'rt-005',
    name: 'Engineering Team Assessment - DataFlow',
    description: 'Team of 15 engineers assessing prioritization across six kinds of items',
    basedOnTemplate: 'dt-003',
    templateName: 'Six Kinds Assessment Template',
    instanceType: 'Team Assessment Session',
    client: 'DataFlow Systems',
    date: '2024-09-15',
    participants: 15,
    outcomes: [
      'Discovered strong performance in Things and Time domains',
      'Identified critical gap in People domain (stakeholder engagement)',
      'Created 60-day improvement plan focused on People & Information',
      'Scheduled quarterly re-assessment'
    ],
    evidenceMetrics: {
      'People domain score': 4.2,
      'Processes domain score': 6.8,
      'Things domain score': 8.1,
      'Activities domain score': 7.3,
      'Information domain score': 5.9,
      'Time domain score': 8.4
    },
    status: 'completed'
  },

  // From STARS Context Assessment
  {
    id: 'rt-006',
    name: 'ReOrg Context Diagnostic - MegaCorp',
    description: 'Leadership team identifying which STARS mode they\'re in during reorganization',
    basedOnTemplate: 'dt-007',
    templateName: 'STARS Context Assessment',
    instanceType: 'Executive Diagnostic',
    client: 'MegaCorp',
    date: '2024-10-08',
    participants: 6,
    outcomes: [
      'Confirmed operating in "Launching a Turnaround" mode',
      'Adjusted prioritization criteria for turnaround context',
      'Adopted turnaround-specific tools and cadences',
      'Created transition plan for when context shifts to "Sustaining"'
    ],
    feedback: 'Naming our context as a turnaround completely changed how we approached prioritization. Game changer.',
    status: 'completed'
  },

  // From Tool Selection Framework
  {
    id: 'rt-007',
    name: 'Tool Audit & Selection - StartupXYZ',
    description: 'Startup selecting prioritization tools based on function family needs',
    basedOnTemplate: 'dt-008',
    templateName: 'Tool Selection Framework',
    instanceType: 'Consulting Engagement',
    client: 'StartupXYZ',
    date: '2024-09-22',
    participants: 5,
    outcomes: [
      'Identified need for Sensemaking Guides and Tradeoff Balancers',
      'Selected 3 tools that map to highest-priority function families',
      'Deprecated 4 tools that weren\'t serving current needs',
      'Reduced tool sprawl from 12 to 5 core tools'
    ],
    evidenceMetrics: {
      'Tools evaluated': 12,
      'Tools selected': 5,
      'Tools deprecated': 4,
      'Estimated time savings': '8 hours/week',
      'Team satisfaction increase': '+34%'
    },
    status: 'completed'
  },

  // From Project Scoping Worksheet
  {
    id: 'rt-008',
    name: 'New Platform Initiative Scoping',
    description: 'Product team using 5W+Extent to scope major platform initiative',
    basedOnTemplate: 'dt-010',
    templateName: 'Project Scoping Worksheet',
    instanceType: 'Planning Workshop',
    client: 'Internal - Peak Priorities',
    date: '2024-10-12',
    participants: 7,
    outcomes: [
      'Comprehensive scope definition in 90 minutes',
      'Identified 5 key stakeholder groups (Who)',
      'Clarified 3 core deliverables vs. 7 nice-to-haves (What)',
      'Set realistic boundaries on scale and depth (To-What-Extent)',
      'Prevented scope creep before project started'
    ],
    feedback: 'The To-What-Extent dimension saved us from trying to boil the ocean.',
    status: 'completed'
  },

  // From Strategy Visualization Templates
  {
    id: 'rt-009',
    name: 'Annual Strategy Presentation - HealthTech',
    description: 'C-suite using four-level diagrams to communicate 2025 strategy',
    basedOnTemplate: 'dt-009',
    templateName: 'Strategy Visualization Template Set',
    instanceType: 'Board Presentation',
    client: 'HealthTech Innovations',
    date: '2024-11-05',
    participants: 45,
    outcomes: [
      'Vision Map presented to board of directors',
      'Architecture Map shared with VP-level leadership',
      'Process Flow Maps distributed to functional teams',
      'Implementation Blueprints given to project managers',
      'Board approved strategy with zero clarifying questions needed'
    ],
    feedback: 'First time our strategy was crystal clear at every level. The four-level approach was transformative.',
    evidenceMetrics: {
      'Board approval': 'unanimous',
      'Clarifying questions': 0,
      'Leadership alignment score': '9.1/10',
      'Time to approval': '45 minutes (vs. 2.5 hours typical)'
    },
    status: 'completed'
  },

  // From Team Capability Development Playbook
  {
    id: 'rt-010',
    name: 'Q4 2024 Capability Sprint - Design Team',
    description: 'Design team running 90-day capability development program',
    basedOnTemplate: 'dt-006',
    templateName: 'Team Capability Development Playbook',
    instanceType: 'Team Development Program',
    client: 'Internal - Peak Priorities',
    date: '2024-10-01',
    participants: 8,
    outcomes: [
      'Baseline assessment completed (avg 6.2/10)',
      '4 weeks in: Situational Awareness +1.3 points',
      'Weekly check-ins maintaining 95% attendance',
      'Mid-point reassessment scheduled for Week 6'
    ],
    status: 'in-progress',
    evidenceMetrics: {
      'Baseline overall score': 6.2,
      'Current overall score': 6.8,
      'Weeks completed': 4,
      'Attendance rate': '95%',
      'Exercises completed': 16
    }
  },

  // From Mode-Based Meeting Templates
  {
    id: 'rt-011',
    name: 'Weekly Leadership Sync - Various Modes',
    description: 'Leadership team rotating through mode-appropriate meeting formats',
    basedOnTemplate: 'dt-011',
    templateName: 'Mode-Based Meeting Agenda Templates',
    instanceType: 'Recurring Meeting Series',
    client: 'CloudScale Solutions',
    date: '2024-09-01',
    participants: 12,
    outcomes: [
      '12 weeks of mode-specific meetings completed',
      'Strategy Mode (weeks 1, 5, 9): Long-term vision alignment',
      'Prioritization Mode (weeks 2, 6, 10): Quarterly ranking decisions',
      'Planning Mode (weeks 3, 7, 11): Sprint and resource planning',
      'Delivery Mode (weeks 4, 8, 12): Execution reviews and blockers',
      'Meeting effectiveness score improved from 5.8 to 8.4'
    ],
    evidenceMetrics: {
      'Meeting effectiveness': '8.4/10 (from 5.8)',
      'Decision clarity': '9.1/10 (from 6.2)',
      'Time efficiency': '+37%',
      'Participant satisfaction': '88%'
    },
    status: 'in-progress'
  },

  // From Team Development Curriculum
  {
    id: 'rt-012',
    name: 'Prioritization Mastery Cohort 1',
    description: 'First cohort of 25 professionals completing 12-week training program',
    basedOnTemplate: 'dt-012',
    templateName: 'Team Development Curriculum',
    instanceType: 'Online Course',
    date: '2024-09-10',
    participants: 25,
    outcomes: [
      'Week 9 of 12 completed',
      'Average module completion: 94%',
      'Contextual Awareness modules: 9.2/10 satisfaction',
      'Execution Rhythms modules: 8.7/10 satisfaction',
      'Relational Navigation modules (in progress): 8.9/10 so far',
      '3 participants already applying learnings in client work'
    ],
    evidenceMetrics: {
      'Enrollment': 25,
      'Active participants': 24,
      'Completion rate': '94%',
      'Average satisfaction': '8.9/10',
      'Capstone projects submitted': 0
    },
    status: 'in-progress'
  }
];

export const getInstancesByTemplate = (templateId: string) => {
  return runTimeInstances.filter(instance => instance.basedOnTemplate === templateId);
};
